
import React, { ReactNode } from 'react';
import { Toaster } from '@/components/ui/toaster';
import { useToast } from '@/hooks/use-toast';
import { useLogoUrl } from '@/hooks/useLogoUrl';
import { SidebarProvider } from '@/components/ui/sidebar';
import SuperAdminSidebar from './SuperAdminSidebar';
import { Button } from '@/components/ui/button';
import { LogOut } from 'lucide-react';

interface SuperAdminLayoutProps {
  children: ReactNode;
  isLoading?: boolean;
  onLogout: () => void;
  activeTab?: string;
  userRole?: string;
}

const SuperAdminLayout: React.FC<SuperAdminLayoutProps> = ({
  children,
  isLoading = false,
  onLogout,
  userRole = 'SuperAdmin'
}) => {
  const { toast } = useToast();
  const logoUrl = useLogoUrl();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-blue"></div>
      </div>
    );
  }

  const handleLogout = () => {
    console.log("SuperAdminLayout: Initiating logout");
    toast({
      title: "Logging out",
      description: "You are being logged out...",
    });
    onLogout();
  };

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <SuperAdminSidebar />
        
        <div className="flex-1 flex flex-col">
          <header className="bg-white border-b px-6 py-3 flex justify-between items-center">
            <div className="flex items-center space-x-4">
              {logoUrl ? (
                <img src={logoUrl} alt="Logo" className="h-8" />
              ) : (
                <span className="text-xl font-bold">Super Admin</span>
              )}
              <span className="text-sm font-medium text-gray-500">{userRole}</span>
            </div>
            
            <Button
              variant="destructive"
              size="sm"
              onClick={handleLogout}
              className="flex items-center gap-2"
            >
              <LogOut className="h-4 w-4" />
              Logout
            </Button>
          </header>

          <main className="flex-1 overflow-y-auto bg-gray-50 p-6">
            {children}
          </main>

          <Toaster />
        </div>
      </div>
    </SidebarProvider>
  );
};

export default SuperAdminLayout;
